import type { ReactNode } from 'react';
import { givenName, type AssistantPendingDTO } from '@contracts/assistant';
import type { MissionDiffValue } from '@contracts/assistant-missions';
import type { Id } from '@contracts/common';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useAssistantOptional } from './AssistantProvider';
import { Hana } from './Hana';
import { useOwnAppearance } from './api';
import { PILL, PILL_TONE } from './ingest/BatchRows';
import { PendingWhy } from './PendingWhy';
import { DIFF_CARD, diffChanges, doerLine } from './missions/labels';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';

/**
 * The `missionDiff` card of `mission.revise` (S7 §6.6, D131): what a run asks to change in the plan, in the S5 batch
 * card's frame and pills — Mới for an added step, Đổi for a changed one (each field before → after), Bỏ for a removed
 * one. The diff is approved or turned down whole: no "Sửa", no boxes. The same card in the chat and in a run's card,
 * whose `onAct` goes to the run's routes.
 */

type Kind = 'add' | 'update' | 'remove';
/** The batch card's tone of each kind of row (S5 §8.1). */
const TONE: Record<Kind, keyof typeof PILL_TONE> = { add: 'create', update: 'update', remove: 'rejected' };
const MUTED = 'text-[hsl(var(--h-muted-foreground))]';

function Row({ kind, title, children }: { kind: Kind; title: string; children?: ReactNode }) {
  return (
    <li data-diff-row={kind}
      className="flex items-start gap-2 border-t border-dashed border-[hsl(var(--h-border))] px-3 py-1.5 first:border-t-0">
      <span data-pill="" className={cn(PILL, PILL_TONE[TONE[kind]], 'mt-px shrink-0')}>{DIFF_CARD.pill[kind]}</span>
      <div className="min-w-0 flex-1 leading-normal">
        <span className={cn('text-[hsl(var(--h-card-foreground))] [overflow-wrap:anywhere]',
          kind === 'remove' && 'line-through opacity-70')}>{title}</span>
        {children}
      </div>
    </li>
  );
}

export function MissionDiffCard({ pending, value, disabled, onAct, className, aside }: {
  pending: AssistantPendingDTO; value: MissionDiffValue; disabled: boolean;
  onAct: (actionId: Id, kind: 'confirm' | 'reject') => void; className?: string; aside?: ReactNode;
}) {
  const appearance = useOwnAppearance();
  const assistant = useAssistantOptional();
  const persona = assistant?.prefs.personaName ?? DEFAULT_PERSONA_NAME;
  return (
    <section role="group" aria-label={DIFF_CARD.heading} data-action={pending.actionId} data-mission-diff=""
      className={cn('mr-auto w-full min-w-0 max-w-[92%] overflow-hidden rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] text-sm', className)}>
      <div className="flex items-center gap-2 border-b border-[hsl(var(--h-border))] px-3 py-2.5">
        <Hana appearance={appearance} emotion="ask" size={24} still={assistant?.still ?? true} className="shrink-0" />
        <b className="min-w-0 flex-1 font-bold text-[hsl(var(--h-card-foreground))] [overflow-wrap:anywhere]">{pending.summary}</b>
      </div>
      {/* S6 §7.5, S7 §9.5: the taint line and why autonomy asked, as on every internal card (D226). */}
      <PendingWhy pending={pending} inset className="pt-1" />
      <ul className="py-1">
        {value.add.map((s) => (
          <Row key={`add:${s.ref}`} kind="add" title={s.title}>
            <div className={cn('mt-0.5 text-xs', MUTED)}>
              {`${doerLine(s, persona, givenName)} · ${DIFF_CARD.under(s.parent.title)}`}
            </div>
          </Row>
        ))}
        {value.update.map((u) => (
          <Row key={`update:${u.itemId}`} kind="update" title={u.title}>
            <ul className={cn('mt-0.5 text-xs', MUTED)}>
              {diffChanges(u.before, u.after, persona, givenName).map((c) => (
                <li key={c.key} data-change={c.key}>{c.text}</li>
              ))}
            </ul>
          </Row>
        ))}
        {value.remove.map((r) => <Row key={`remove:${r.itemId}`} kind="remove" title={r.title} />)}
      </ul>
      <div className="flex flex-wrap items-center gap-2 border-t border-[hsl(var(--h-border))] px-3 py-2.5">
        <Button type="button" size="sm" disabled={disabled} onClick={() => onAct(pending.actionId, 'confirm')}>
          {DIFF_CARD.approve}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={disabled}
          onClick={() => onAct(pending.actionId, 'reject')}>{DIFF_CARD.reject}</Button>
        <span className="flex-1" />
        {aside !== undefined && <span className={cn('text-xs', MUTED)}>{aside}</span>}
      </div>
    </section>
  );
}
